import { AnnotationStatus } from "@faro-lotv/ielement-types";
import { getMarkupAssigneeMember } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getPrettyName } from "@utils/user-utils";
/** Search term that matches the markups assigned to the current user */
export const CURRENT_USER_SEARCH_TERM = "me";

/** Headers for the project markups table */
export let ProjectMarkupsTableHeaders = /*#__PURE__*/function (ProjectMarkupsTableHeaders) {
  /** Markup name */
  ProjectMarkupsTableHeaders["name"] = "Name";
  /** Markup assignee */
  ProjectMarkupsTableHeaders["assignee"] = "Assignee";
  /** Member who created the markup */
  ProjectMarkupsTableHeaders["creator"] = "Created By";
  /** Markup due date */
  ProjectMarkupsTableHeaders["dueDate"] = "Due Date";
  /** Markup status */
  ProjectMarkupsTableHeaders["status"] = "Status";
  /** Markup options */
  ProjectMarkupsTableHeaders["options"] = "Options";
  /** Markup tags */
  ProjectMarkupsTableHeaders["tags"] = "Tags";
  /** Markup attachments */
  ProjectMarkupsTableHeaders["attachments"] = "Attachments";
  /** When the markup was created */
  ProjectMarkupsTableHeaders["createdAt"] = "Created";
  /** When the markup or one of its fields was last modified */
  ProjectMarkupsTableHeaders["lastUpdatedAt"] = "Last Updated";
  /** When the markup was last assigned */
  ProjectMarkupsTableHeaders["assignedAt"] = "Assigned";
  /** When the due date of the markup was last set */
  ProjectMarkupsTableHeaders["dueDateSetAt"] = "Due Date Set";
  return ProjectMarkupsTableHeaders;
}({});

/** Columns of the project markups table, in their default order */
export const PROJECT_MARKUPS_TABLE_COLUMNS = [ProjectMarkupsTableHeaders.name, ProjectMarkupsTableHeaders.assignee, ProjectMarkupsTableHeaders.creator, ProjectMarkupsTableHeaders.dueDate, ProjectMarkupsTableHeaders.status, ProjectMarkupsTableHeaders.tags, ProjectMarkupsTableHeaders.attachments, ProjectMarkupsTableHeaders.createdAt, ProjectMarkupsTableHeaders.lastUpdatedAt, ProjectMarkupsTableHeaders.assignedAt, ProjectMarkupsTableHeaders.dueDateSetAt, ProjectMarkupsTableHeaders.options];
const HIDDEN_BY_DEFAULT_COLUMNS = [ProjectMarkupsTableHeaders.creator, ProjectMarkupsTableHeaders.createdAt, ProjectMarkupsTableHeaders.lastUpdatedAt, ProjectMarkupsTableHeaders.assignedAt, ProjectMarkupsTableHeaders.dueDateSetAt];

/** Columns shown until the user customizes them */
export const PROJECT_MARKUPS_TABLE_DEFAULT_VISIBLE_COLUMNS = PROJECT_MARKUPS_TABLE_COLUMNS.filter(column => !HIDDEN_BY_DEFAULT_COLUMNS.includes(column));

/** Labels of the columns in the column visibility menu, which are the headers themselves */
export const PROJECT_MARKUPS_TABLE_COLUMN_LABELS = Object.fromEntries(PROJECT_MARKUPS_TABLE_COLUMNS.map(column => [column, column]));

/** The grid counts the checkbox column, which comes first, in the column indices */
const CHECKBOX_COLUMN_OFFSET = 1;

/**
 * Returns where a column that is only shown goes: the creator after the assignee, or the name
 * when the assignee is hidden, and any other one before the options, which stay last
 */
function getShownColumnIndex(shownColumns, column) {
  const {
    name,
    assignee,
    creator,
    options
  } = ProjectMarkupsTableHeaders;
  if (column === creator) {
    const assigneeIndex = shownColumns.indexOf(assignee);
    return (assigneeIndex === -1 ? shownColumns.indexOf(name) : assigneeIndex) + 1;
  }
  const optionsIndex = shownColumns.indexOf(options);
  return optionsIndex === -1 ? shownColumns.length : optionsIndex;
}

/**
 * Returns the columns to show: the visible ones plus the forced ones, e.g. the creator while the
 * table is filtered by creator or the column it is sorted by, without storing them as visible
 */
export function getShownMarkupsColumns(visibleColumns, forcedColumns) {
  return forcedColumns.reduce((shownColumns, column) => {
    if (shownColumns.includes(column)) {
      return shownColumns;
    }
    const index = getShownColumnIndex(shownColumns, column);
    return [...shownColumns.slice(0, index), column, ...shownColumns.slice(index)];
  }, visibleColumns);
}

/**
 * Maps a column move in the grid, which shows the shown columns, to the visible columns.
 * Returns undefined when the moved column is only shown, so its position is not stored.
 */
export function getVisibleColumnMove(shownColumns, visibleColumns, {
  oldIndex,
  targetIndex
}) {
  const movedColumn = shownColumns[oldIndex - CHECKBOX_COLUMN_OFFSET];
  if (!visibleColumns.includes(movedColumn)) {
    return undefined;
  }
  const newShownColumns = shownColumns.filter(column => column !== movedColumn);
  newShownColumns.splice(targetIndex - CHECKBOX_COLUMN_OFFSET, 0, movedColumn);
  const newVisibleColumns = newShownColumns.filter(column => visibleColumns.includes(column));
  return {
    oldIndex: visibleColumns.indexOf(movedColumn) + CHECKBOX_COLUMN_OFFSET,
    targetIndex: newVisibleColumns.indexOf(movedColumn) + CHECKBOX_COLUMN_OFFSET
  };
}

/** Order of the statuses when the markups are sorted by status */
export const MARKUP_STATUS_ORDER = [AnnotationStatus.Unclassified, AnnotationStatus.Open, AnnotationStatus.InProgress, AnnotationStatus.ToReview, AnnotationStatus.Resolved];
/**
 * Returns the text the quick search of the table looks into: the markup name, the name and email of
 * its assignee and, for the markups assigned to the current user, the current user search term.
 * Searching for that term only looks into the names and the term, as it is part of many names and emails.
 */
export function getMarkupQuickSearchText({
  markup,
  companyMembers,
  projectMembers,
  currentUser,
  quickFilter
}) {
  // Only the first assignee is considered, same as the assignee column
  const isAssignedToCurrentUser = !!currentUser && markup.assignee?.values?.[0] === currentUser.identity;
  const currentUserTerm = isAssignedToCurrentUser ? CURRENT_USER_SEARCH_TERM : "";

  // A line break can't be typed in the search box, so a search never matches across two values
  if (quickFilter.trim().toLowerCase() === CURRENT_USER_SEARCH_TERM) {
    return [markup.name, currentUserTerm].join("\n");
  }
  // The member lists may miss the current user, e.g. without permission to see the workspace members
  const assignee = getMarkupAssigneeMember({
    assignee: markup.assignee,
    companyMembers,
    projectMembers
  }) ?? (isAssignedToCurrentUser ? currentUser : undefined);
  return [markup.name, getPrettyName(assignee), assignee?.email ?? "", currentUserTerm].join("\n");
}